import { IS_BROWSER } from '@smart-tv/utils/is-browser';

// variables
export const NAVIGATION_HEIGHT = 152;
export const TV_GUIDE_HEADER_HEIGHT = 290;
export const MINUTES_OF_A_FULL_DAY = 24 * 60;
export const MINUTE_WIDTH = 14;
export const PROGRAM_HEIGHT = 76;
export const PROGRAM_GAP = 16;
export const PROGRAM_W_GAP = 8;
export const TIME_SPAN_HEIGHT = 40 + PROGRAM_GAP;
export const TIME_SPAN_WIDTH = MINUTE_WIDTH * 30;
export const CHANNEL_WIDTH = PROGRAM_HEIGHT * 1.5;
export const GRID_SPACING = 88;
export const GRID_GAP = 12;
export const GRID_OFFSET = CHANNEL_WIDTH + GRID_GAP;

// ── Responsive scaling ────────────────────────────────────────────────────────

/** The viewport width at which scale = 1 (matches the $breakpoint-xl design target). */
export const BASE_VIEWPORT = 1920;

export const MIN_VIEWPORT = 880;

/** The minimum allowed scale factor — grid never shrinks below 65 % of its base size. */
export const MIN_SCALE = 0.65;

export const MAX_SCALE = IS_BROWSER ? 0.65 : 1;

/**
 * Computes a viewport-proportional scale clamped to [MIN_SCALE, 1], mirroring the
 * fluid-prop-range curve used for font sizes, then scaled by MAX_SCALE.
 *
 * Why clamp:
 * - upper clamp (1) prevents accidental upscale on very large displays
 * - lower clamp (0.65) keeps touch/focus targets and text legible
 *
 * Example:
 * - 1920 px -> 1.0
 * - 1440 px -> 0.75
 * - 1000 px -> 0.65 (clamped)
 */
export function getTvGuideScale(viewportWidth: number): number {
  const progress = (viewportWidth - MIN_VIEWPORT) / (BASE_VIEWPORT - MIN_VIEWPORT);
  return MAX_SCALE * (MIN_SCALE + (1 - MIN_SCALE) * Math.min(1, Math.max(0, progress)));
}

/** All pixel layout constants scaled to the current viewport. */
export interface ScaledTvGuideConstants {
  minuteWidth: number;
  programHeight: number;
  programGap: number;
  programWGap: number;
  channelWidth: number;
  gridSpacing: number;
  gridGap: number;
  gridOffset: number;
  timeSpanWidth: number;
}

/** Derives every layout constant from the raw base values × scale. */
export function getScaledConstants(scale: number): ScaledTvGuideConstants {
  // All layout math for X/Y coordinates, widths, and spacing must use the same scale
  // source, otherwise elements drift out of alignment (for example: timeline ticks,
  // program blocks, live marker, and viewport culling).
  const minuteWidth = MINUTE_WIDTH * scale;
  const programHeight = PROGRAM_HEIGHT * scale;
  const programGap = PROGRAM_GAP * scale;
  const programWGap = PROGRAM_W_GAP * scale;
  const channelWidth = CHANNEL_WIDTH * scale;
  const gridSpacing = GRID_SPACING * scale;
  const gridGap = GRID_GAP * scale;
  return {
    minuteWidth,
    programHeight,
    programGap,
    programWGap,
    channelWidth,
    gridSpacing,
    gridGap,
    gridOffset: channelWidth + gridGap,
    timeSpanWidth: minuteWidth * 30
  };
}

export const LIVE_MARKER = 6;

export const DATEPICKER_SCROLL_HEIGHT = 352;

export const NOW_FOCUS_KEY = 'NOW_FOCUS_KEY';
export const DATE_FOCUS_KEY = 'TVGuideDate';
export const FAVORITES_FOCUS_KEY = 'TVGuideFavorites';

/**
 * an array of formatted timestrings of the current day with intervals of 30 minutes. i.e. ['00.00', '00.30', '01.00', ...]
 */
export const timeStringsOfAFullDay = [...Array(48)].map(
  (_, index) => `${('0' + Math.floor(index / 2)).slice(-2)}:${index % 2 === 0 ? '00' : '30'}`
);

/**
 * Get the scroll position to center the current time in the TV guide grid
 */
export const getCurrentTimeScrollPosition = (
  elapsedMinutes: number,
  minuteWidth: number = MINUTE_WIDTH
): number => {
  // The guide's horizontal coordinate system is measured in "minute pixels".
  // elapsedMinutes * minuteWidth gives the current-time X coordinate inside the 24h grid.
  // The remaining terms align that point with the same visual offset used by timeline cells.
  return elapsedMinutes * minuteWidth - window.innerWidth / 2.5 + minuteWidth * 30;
};
